import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface InvoiceData {
  orderId: string;
  orderDate: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  address: any;
  items: any[];
  additionalCharges?: Array<{
    description?: string;
    name?: string;
    amount: number;
  }>;
  payment: {
    subtotal: number;
    tax: number;
    total: number;
    method: string;
    status?: string;
    paidAmount?: number;
  };
  scheduling?: {
    date: string;
    time: string;
  };
  timeline?: {
    placed?: any;
    confirmed?: any;
    started?: any;
    completed?: any;
  };
  status?: string;
  providerName?: string;
  providerPhone?: string;
}

export const generateInvoicePDF = (invoiceData: InvoiceData) => {
  try {
    const doc = new jsPDF();
    
    // Company Header
    doc.setFontSize(24);
    doc.setTextColor(79, 70, 229); // primary-600
    doc.text('RAPSAPP', 20, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('Home Services Platform', 20, 27);
  doc.text('Joginder Nagar, Mandi, Himachal Pradesh', 20, 32);
  doc.text('Phone: +91 7018401540', 20, 37);
  doc.text('Email: rapsappservice@gmail.com', 20, 42);
  
  // Invoice Title
  doc.setFontSize(20);
  doc.setTextColor(0, 0, 0);
  doc.text('INVOICE', 150, 20);
  
  // Invoice Details
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Invoice #: ${invoiceData.orderId.slice(-8)}`, 150, 27);
  doc.text(`Date: ${new Date(invoiceData.orderDate).toLocaleDateString()}`, 150, 32);
  doc.text(`Payment: ${invoiceData.payment.method.toUpperCase()}`, 150, 37);
  
  if (invoiceData.payment.status) {
    const statusColor = invoiceData.payment.status === 'completed' ? [34, 197, 94] : [239, 68, 68];
    doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.text(`Status: ${invoiceData.payment.status.toUpperCase()}`, 150, 42);
  }
  
  // Customer Details
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Bill To:', 20, 55);
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(invoiceData.customerName, 20, 62);
  doc.text(invoiceData.customerEmail, 20, 67);
  doc.text(invoiceData.customerPhone, 20, 72);
  
  // Service Address
  let addressEndY = 82;
  if (invoiceData.address) {
    doc.text('Service Address:', 20, 82);
    const addressLines = [
      invoiceData.address.houseNumber,
      invoiceData.address.streetAddress,
      invoiceData.address.landmark,
      `${invoiceData.address.city}, ${invoiceData.address.state}`,
      invoiceData.address.pincode
    ].filter(Boolean);
    
    let yPos = 87;
    addressLines.forEach(line => {
      doc.text(line, 20, yPos);
      yPos += 5;
    });
    addressEndY = yPos;
  }
  
  // Service Schedule
  if (invoiceData.scheduling) {
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Scheduled Service:', 120, 82);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${invoiceData.scheduling.date}`, 120, 87);
    doc.text(`Time: ${invoiceData.scheduling.time}`, 120, 92);
  }
  
  // Service Provider (if available)
  let providerYPos = invoiceData.scheduling ? 100 : 82;
  if (invoiceData.providerName || invoiceData.providerPhone) {
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Service Provider:', 120, providerYPos);
    doc.setTextColor(100, 100, 100);
    if (invoiceData.providerName) {
      doc.text(invoiceData.providerName, 120, providerYPos + 5);
    }
    if (invoiceData.providerPhone) {
      doc.text(invoiceData.providerPhone, 120, providerYPos + 10);
    }
  }
  
  // Status Timeline (if available)
  let timelineStartY = Math.max(addressEndY + 5, 115);
  if (invoiceData.timeline && Object.keys(invoiceData.timeline).length > 0) {
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Order Timeline:', 20, timelineStartY);
    
    doc.setFontSize(9);
    let currentY = timelineStartY + 6;
    
    if (invoiceData.timeline.placed) {
      const placedDate = new Date(invoiceData.timeline.placed);
      doc.setTextColor(59, 130, 246); // Blue
      doc.text('● Order Placed:', 25, currentY);
      doc.setTextColor(100, 100, 100);
      doc.text(`${placedDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${placedDate.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}`, 60, currentY);
      currentY += 5;
    }
    
    if (invoiceData.timeline.confirmed) {
      const confirmedDate = new Date(invoiceData.timeline.confirmed);
      doc.setTextColor(99, 102, 241); // Indigo
      doc.text('● Order Confirmed:', 25, currentY);
      doc.setTextColor(100, 100, 100);
      doc.text(`${confirmedDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${confirmedDate.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}`, 60, currentY);
      currentY += 5;
    }
    
    if (invoiceData.timeline.started) {
      const startedDate = new Date(invoiceData.timeline.started);
      doc.setTextColor(168, 85, 247); // Purple
      doc.text('● Service Started:', 25, currentY);
      doc.setTextColor(100, 100, 100);
      doc.text(`${startedDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${startedDate.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}`, 60, currentY);
      currentY += 5;
    }
    
    if (invoiceData.timeline.completed) {
      const completedDate = new Date(invoiceData.timeline.completed);
      doc.setTextColor(34, 197, 94); // Green
      doc.text('● Service Completed:', 25, currentY);
      doc.setTextColor(100, 100, 100);
      doc.text(`${completedDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${completedDate.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}`, 60, currentY);
      currentY += 5;
    }
    
    timelineStartY = currentY + 5;
  }
  
  // Items Table
  const tableStartY = Math.max(timelineStartY, invoiceData.address ? 130 : 115);
  
  const tableData = invoiceData.items.map((item, index) => {
    const addonsText = item.addons && item.addons.length > 0
      ? `\nAdd-ons: ${item.addons.map((a: any) => a.name).join(', ')}`
      : '';
    
    return [
      index + 1,
      item.name + addonsText,
      item.quantity,
      `₹${item.price.toLocaleString()}`,
      `₹${(item.price * item.quantity).toLocaleString()}`
    ];
  });
  
  (doc as any).autoTable({
    startY: tableStartY,
    head: [['#', 'Service Description', 'Qty', 'Rate', 'Amount']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [79, 70, 229], // primary-600
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 9,
      cellPadding: 5
    },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 80 },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 35, halign: 'right' },
      4: { cellWidth: 35, halign: 'right' }
    }
  });
  
  // Payment Summary
  let finalY = (doc as any).lastAutoTable.finalY + 10;
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  
  // Subtotal
  doc.text('Subtotal:', 130, finalY);
  doc.text(`₹${invoiceData.payment.subtotal.toLocaleString()}`, 185, finalY, { align: 'right' });
  finalY += 6;
  
  // Tax (if exists)
  if (invoiceData.payment.tax > 0) {
    doc.text('GST (18%):', 130, finalY);
    doc.text(`₹${invoiceData.payment.tax.toLocaleString()}`, 185, finalY, { align: 'right' });
    finalY += 6;
  }
  
  // Additional Charges (if exists)
  if (invoiceData.additionalCharges && invoiceData.additionalCharges.length > 0) {
    doc.setFontSize(10);
    doc.setTextColor(200, 100, 0); // Orange color
    doc.text('Additional Charges:', 130, finalY);
    finalY += 5;
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    invoiceData.additionalCharges.forEach((charge) => {
      const description = charge.description || charge.name || 'Additional Service';
      doc.text(`  • ${description}`, 130, finalY);
      doc.text(`₹${charge.amount.toLocaleString()}`, 185, finalY, { align: 'right' });
      finalY += 5;
    });
    finalY += 1;
  }
  
  // Total
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('Total Amount:', 130, finalY);
  doc.text(`₹${invoiceData.payment.total.toLocaleString()}`, 185, finalY, { align: 'right' });
  
  // Payment Status
  finalY += 8;
  doc.setFontSize(10);
  if (invoiceData.payment.status === 'completed') {
    doc.setTextColor(34, 197, 94); // Green
    doc.text('✓ PAID IN FULL', 130, finalY);
  } else {
    const paidAmount = invoiceData.payment.paidAmount || 0;
    const unpaidAmount = invoiceData.payment.total - paidAmount;
    
    if (paidAmount > 0) {
      doc.setTextColor(100, 100, 100);
      doc.text(`Paid: ₹${paidAmount.toLocaleString()}`, 130, finalY);
      finalY += 5;
      doc.setTextColor(239, 68, 68); // Red
      doc.text(`Pending: ₹${unpaidAmount.toLocaleString()}`, 130, finalY);
    } else {
      doc.setTextColor(239, 68, 68); // Red
      doc.text('PAYMENT PENDING', 130, finalY);
    }
  }
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont('helvetica', 'normal');
  doc.text('Thank you for choosing RAPSAPP!', 105, 280, { align: 'center' });
  doc.text('For support, contact us at rapsappservice@gmail.com or +91 7018401540', 105, 285, { align: 'center' });
  
  // Save the PDF
  const filename = `RAPSAPP-Invoice-${invoiceData.orderId.slice(-8)}.pdf`;
  console.log('✅ Saving PDF:', filename);
  doc.save(filename);
  console.log('✅ PDF saved successfully!');
  } catch (error) {
    console.error('❌ Error generating PDF:', error);
    throw error;
  }
};

export const shareOrderDetails = async (orderData: any) => {
  console.log('📤 Sharing order details...', orderData);
  
  const shareText = `
🏠 RAPSAPP Booking Confirmation

📋 Order ID: #${orderData.id.slice(-8)}
📅 Booked: ${new Date(orderData.createdAt).toLocaleString()}
${orderData.scheduling ? `⏰ Service Date: ${orderData.scheduling.date} at ${orderData.scheduling.time}` : ''}

💰 Total Amount: ₹${orderData.payment?.total?.toLocaleString() || '0'}
💳 Payment: ${orderData.payment?.method?.toUpperCase() || 'N/A'}

🛠️ Services:
${orderData.items?.map((item: any, i: number) => `${i + 1}. ${item.name} (Qty: ${item.quantity})`).join('\n')}

📍 Service Address:
${orderData.address?.fullName}
${orderData.address?.phone}
${[orderData.address?.houseNumber, orderData.address?.streetAddress, orderData.address?.city].filter(Boolean).join(', ')}

Visit: www.rapsapp.in
Contact: +91 7018401540
  `.trim();

  console.log('📝 Share text prepared');

  if (navigator.share) {
    console.log('✅ Web Share API available');
    try {
      await navigator.share({
        title: `RAPSAPP Order #${orderData.id.slice(-8)}`,
        text: shareText,
      });
      console.log('✅ Share completed successfully');
      return { success: true };
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('❌ Error sharing:', error);
        return { success: false, error };
      }
      console.log('ℹ️ Share cancelled by user');
      return { success: false, cancelled: true };
    }
  } else {
    console.log('ℹ️ Web Share API not available, using clipboard fallback');
    // Fallback: Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      console.log('✅ Copied to clipboard successfully');
      return { success: true, copied: true };
    } catch (error) {
      console.error('❌ Error copying to clipboard:', error);
      return { success: false, error };
    }
  }
};
